import React, { Component, ReactNode } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class SecurityErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: React.ErrorInfo) {
    // Siempre loggear errores críticos que causan pantalla negra
    try {
      const msg = typeof error?.message === 'string' ? error.message : String(error);
      console.error('🚨 CRITICAL ERROR:', msg);
      console.error('Error Stack:', error?.stack);
      console.error('Component Stack:', errorInfo?.componentStack);
      
      // Intentar limpiar datos corruptos que puedan estar causando el error
      if (msg.includes('localStorage') || msg.includes('storage') || msg.includes('parse')) {
        console.warn('🧹 Detected storage error, attempting cleanup...');
        try {
          // Limpiar solo datos problemáticos, no toda la sesión
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.includes('selectedCompany') || key.includes('validator')) {
              try {
                localStorage.removeItem(key);
                console.log('Removed corrupted key:', key);
              } catch (e) {
                // ignore
              }
            }
          });
        } catch (cleanupError) {
          console.error('Cleanup failed:', cleanupError);
        }
      }
    } catch (loggingError) {
      console.error('Error in error handler:', loggingError);
    }
  }

  handleRetry = () => {
    // Solo reiniciar el estado del error boundary, sin reload completo
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="container max-w-md">
            <Alert className="border-destructive/50 bg-destructive/10">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="space-y-4">
                <div>
                  <strong>Error de Inicialización</strong><br/>
                  Hubo un problema al cargar la aplicación. 
                  Esto puede deberse a datos corruptos en el navegador.
                </div>
                {this.state.error && (
                  <div className="text-xs text-muted-foreground font-mono bg-muted p-2 rounded">
                    {this.state.error.message}
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={this.handleRetry}
                    size="sm"
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reiniciar Sistema
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/auth'}
                    size="sm"
                    variant="outline"
                  >
                    Ir al Login
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}